import { HttpErrorResponse } from '@angular/common/http';
import * as Sentry from '@sentry/angular';
import { toHttpRequestError } from './sentry-http-error';

/**
 * The three things the funnel tells Sentry about *who* and *what* an event
 * happened to, plus the one way it reports a failure it has already handled.
 *
 * Everything here is a no-op when `environment.sentry.enabled` is false and
 * `Sentry.init` was therefore never called (local dev): the SDK's top-level
 * functions write to a scope that has no client attached to flush it.
 */

/**
 * Tag the trip and reservation the customer is currently booking.
 *
 * Without these, "which reservation broke" is unanswerable from an issue —
 * before this every funnel issue reported `Users Impacted: 0` and carried no
 * booking identity at all. Tags rather than context so they are indexed and can
 * be filtered and grouped on in the issue list.
 */
export function setBookingContext(tripId: number | null, reservationId: number | null): void {
  Sentry.setTag('tripId', tripId == null ? undefined : String(tripId));
  Sentry.setTag('reservationId', reservationId == null ? undefined : String(reservationId));
}

/**
 * Identify the signed-in customer, or clear them on sign-out.
 *
 * The id and nothing else. This is a consumer booking funnel: the session also
 * exposes the customer's name, email and phone, and none of it goes to Sentry.
 * The id is enough to count affected customers and to look the person up in the
 * ERP when an issue actually needs chasing.
 */
export function setSignedInCustomer(customerPersonId: number | null): void {
  Sentry.setUser(customerPersonId == null ? null : { id: String(customerPersonId) });
}

/**
 * Report a failure the funnel has already recovered from — a persist that was
 * rolled back, a fallback that was taken.
 *
 * These sites log to the console and carry on, which reads as harmless but is
 * exactly where a customer is left stuck mid-booking: their hotel choice never
 * reached the backend and nothing tells them so. `consoleLoggingIntegration`
 * turns those logs into Sentry *Logs*, a surface that is not alertable the way
 * Issues are, so they were effectively invisible. Reporting them here makes each
 * one an Issue, tagged with the operation that failed.
 *
 * `HttpErrorResponse` is converted first: captured raw it would serialise as an
 * anonymous object and land back in the junk-drawer issue this whole module
 * exists to avoid.
 */
export function reportFailure(operation: string, error: unknown): void {
  Sentry.captureException(error instanceof HttpErrorResponse ? toHttpRequestError(error) : error, {
    tags: { operation },
  });
}

const THROTTLED_REPORT_INTERVAL_MS = 5 * 60_000;
const lastThrottledReportAt = new Map<string, number>();

/**
 * The gate behind {@link reportFailureThrottled}: true for the first call of an
 * operation and again once the interval has passed; false, and nothing
 * remembered, for every call in between. Exported for its spec — the Sentry
 * module cannot be mocked inside the bundled test run.
 */
export function throttledReportDue(operation: string, now = Date.now()): boolean {
  const last = lastThrottledReportAt.get(operation);
  if (last != null && now - last < THROTTLED_REPORT_INTERVAL_MS) return false;
  lastThrottledReportAt.set(operation, now);
  return true;
}

/**
 * {@link reportFailure} for a site that fails in bursts — one WordPress outage
 * is one Issue's worth of events, not one per trip card on screen. At most one
 * event per operation every five minutes; the rest are dropped, not queued.
 */
export function reportFailureThrottled(operation: string, error: unknown): void {
  if (throttledReportDue(operation)) reportFailure(operation, error);
}
